import { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { Stack } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {_createOrdersInvestmentStep} from "../../../utils/_createOrdersInvestmentStep";
import {setInvestment, setPriceStepPercentage} from "../../../store/slices/botSlice";
import {RootState} from "../../../store/store";
import {Order} from "../../../class/Order";
import {Bot} from "../../../_app/types";
import formatNumber from "../../../_utils/formatNumber";
import {precision} from "../../../_utils/precision";
import {_adjustInvestment} from "../../../utils/_adjustInvestment";
import CustomPaper from "../../../_components/CustomPaper";
import {ButtonStyled} from "../../../_components/ButtonStyled";
import IconComponent from "../../../components/ui/IconComponent";
import AdronTypography from "../../../_components/AdronTypography";

interface Props {
  step: number;
  onChangeStep: (step: number) => void;
  bot: Bot;
  orderStep: number;
  onChangeInvestmentInQuote: (investmentInQuote: number) => void;
  onChangeInvestmentInBase: (investmentInBase: number) => void;
  onChangeOrderList: (list: Order[]) => void;
  priceStepQuery: number;
  showPreview: (value: boolean) => void;
  editing: boolean;
}

const FuturesBotPagination = ({
  step,
  onChangeStep,
  bot,
  orderStep,
  onChangeInvestmentInQuote,
  onChangeInvestmentInBase,
  onChangeOrderList,
  priceStepQuery,
  showPreview,
  editing,
}: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const canStartBot = useSelector((state: RootState) => state.user.canStartBot);

  const filledFirstStepFields = useMemo(() => {
    if (bot.minPrice === 0 || bot.maxPrice === 0 || bot.startPrice === 0) {
      return true;
    } else
			return bot.gridDirection === "Central" && bot.gridsAmount % 2 !== 0;
  }, [bot.minPrice, bot.maxPrice, bot.startPrice, bot.gridDirection, bot.gridsAmount]);

  const rangePriceError = useMemo(() => {
      return bot.minPrice < 0 || bot.maxPrice < 0;
    }, [bot.minPrice, bot.maxPrice]);

  const buttonStyle = {
    display: "flex",
    padding: "7px 15px",
    width: "100%",
  };

  const handleDecreaseStep = () => {
    let orderInvStep = _createOrdersInvestmentStep(bot, orderStep, priceStepQuery);
    onChangeOrderList(orderInvStep.orders);
    onChangeInvestmentInBase(orderInvStep.stepInBase);
    onChangeInvestmentInQuote(orderInvStep.stepInQuote);

    const result = orderInvStep.orders.reduce<{ min: number; max: number }>(
      (acc, item) => {
        const value = bot.gridType === "ARITHMETIC" ? item.priceStepPercent * 100 : item.priceStep;
        acc.min = Math.min(acc.min, value);
        acc.max = Math.max(acc.max, value);
        return acc;
      },
      { min: Infinity, max: -Infinity }
    );
    dispatch(
      setPriceStepPercentage(
        bot.gridType === "ARITHMETIC"
          ? `${formatNumber(result.min, precision(bot.ticker.priceStep))}% \u2014 ${formatNumber(result.max, precision(bot.ticker.priceStep))}%`
          : `$${formatNumber(result.min, 4)} \u2014 $${formatNumber(result.max, 4)}`
      )
    );

    if (step === 3) {
      dispatch(
        setInvestment(
          _adjustInvestment(
            bot.investment,
            bot.currencyType === "BASE" ? orderInvStep.stepInBase : orderInvStep.stepInQuote
          )
        )
      );
    }

    onChangeStep(step + 1);
  };
  const handleIncreaseStep = () => onChangeStep(step - 1);

  const handleToPreview = () => {
    showPreview(true);
    let orderInvStep = _createOrdersInvestmentStep(bot, orderStep, priceStepQuery);
    onChangeOrderList(orderInvStep.orders);
  };

  return (
    <CustomPaper
      sx={{
        backgroundColor: theme.palette.background.paper + "!important",
        borderRadius: "12px !important",
      }}
    >
      <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={1}>
        <ButtonStyled
          onClick={step > 1 ? handleIncreaseStep : undefined}
          style={{ opacity: 0.5, ...buttonStyle }}
          fullWidth={true}
          color={"secondary"}
          variant="text"
          startIcon={<IconComponent iconName={"ChevronLeft"} hasStroke hasFill />}
        >
          Back
        </ButtonStyled>
        <AdronTypography
          style={{ width: "100%", textAlign: "center" }}
          halfOpacity
          value={`Step ${step} of  4`}
        />
        {step === 4 ? (
          <ButtonStyled
            onClick={handleToPreview}
            style={{ padding: "8px 16px", color: "#fff" }}
            fullWidth={true}
            color={"primary"}
            variant="contained"
            disabled={!canStartBot}
            sx={{ opacity: !canStartBot ? 0.4 : 1 }}
          >
            Preview
          </ButtonStyled>
        ) : (
          <ButtonStyled
            onClick={step < 4 ? handleDecreaseStep : undefined}
            style={buttonStyle}
            disabled={filledFirstStepFields || rangePriceError}
            fullWidth={true}
            color={"secondary"}
            variant="outlined"
            endIcon={<IconComponent iconName={"ChevronRight"} hasStroke />}
          >
            Next
          </ButtonStyled>
        )}
      </Stack>
    </CustomPaper>
  );
};

export default FuturesBotPagination;
